import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { getProfile } from '../api/auth';

export const AuthContext = createContext({
  user: null,
  setUser: () => {},
  refreshMe: async () => {},
  clearSession: () => {},
  isHydrating: true,
  isPlusActive: false,
  isEnhancedActive: false,
  subscriptionStatus: '',
  planName: '',
  hasFeature: () => false,
});

const FEATURE_REQUIREMENTS = {
  analytics: 'plus',
  payments: 'plus',
  teams: 'plus',
  tenant_portal: 'enhanced',
  jobs: 'enhanced',
  maintenance_requests: 'enhanced',
};

const PLAN_RANK = {
  free: 0,
  plus: 1,
  enhanced: 2,
};

function normalizePlan(value) {
  const raw = String(value || '').toLowerCase();
  if (raw.includes('enhanced')) return 'enhanced';
  if (raw.includes('plus')) return 'plus';
  return 'free';
}

function computeSubscription(user) {
  if (!user) {
    return {
      isPlusActive: false,
      isEnhancedActive: false,
      subscriptionStatus: '',
      planName: 'free',
      features: {},
    };
  }

  const role = (user.role || '').toUpperCase();

  if (role === 'ADMIN') {
    const status =
      (user.subscriptionStatus || user.subscription_status || 'active').toLowerCase();

    return {
      isPlusActive: true,
      isEnhancedActive: true,
      subscriptionStatus: status || 'active',
      planName: 'enhanced',
      features: user.features || {},
    };
  }

  const status =
    (user.subscriptionStatus || user.subscription_status || '').toLowerCase();
  const explicitPlan = normalizePlan(user.rawPlan || user.planName);
  const responsePlan = normalizePlan(user.plan);
  const plan = explicitPlan !== 'free' ? explicitPlan : responsePlan;

  const active = status === 'active' || status === 'trialing';
  const effectivePlan = active ? (plan !== 'free' ? plan : 'plus') : 'free';
  const isPlus = PLAN_RANK[effectivePlan] >= PLAN_RANK.plus;
  const isEnhanced = PLAN_RANK[effectivePlan] >= PLAN_RANK.enhanced;

  return {
    isPlusActive: isPlus,
    isEnhancedActive: isEnhanced,
    subscriptionStatus: status,
    planName: effectivePlan,
    features: user.features || {},
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isHydrating, setIsHydrating] = useState(true);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem('accessToken');

    // 🚨 Critical fix: do NOT hit backend without a token
    if (!token) {
      setUser(null);
      setIsHydrating(false);
      return;
    }

    try {
      const res = await getProfile();

      const u =
        res?.data?.data?.user ??
        res?.data?.user ??
        res?.data ??
        null;

      setUser(u || null);
    } catch (err) {
      console.error('[AuthContext] refreshMe failed', err);
      setUser(null);
    } finally {
      setIsHydrating(false);
    }
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('accessToken'); // 🔥 also clear token
    setUser(null);
  }, []);

  // Initial hydration (only if token exists)
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setIsHydrating(false);
      return;
    }
    refreshMe();
  }, [refreshMe]);

  // Re-hydrate on focus/visibility ONLY if token exists
  useEffect(() => {
    const onFocusOrVisible = () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      refreshMe();
    };

    window.addEventListener('focus', onFocusOrVisible);
    window.addEventListener('visibilitychange', onFocusOrVisible);

    return () => {
      window.removeEventListener('focus', onFocusOrVisible);
      window.removeEventListener('visibilitychange', onFocusOrVisible);
    };
  }, [refreshMe]);

  const subscription = useMemo(() => computeSubscription(user), [user]);

  const value = useMemo(
    () => {
      const hasFeature = (feature) => {
        if (subscription.features && subscription.features[feature] !== undefined) {
          return Boolean(subscription.features[feature]);
        }

        const required = FEATURE_REQUIREMENTS[feature] || 'free';
        return PLAN_RANK[subscription.planName || 'free'] >= PLAN_RANK[required];
      };

      return {
      user,
      setUser,
      refreshMe,
      clearSession,
      isHydrating,
      ...subscription,
      hasFeature,
    };
    },
    [user, refreshMe, clearSession, isHydrating, subscription]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
